import {
  ESFORCO_SEM_VARIANTE,
  type AgenteOpencode,
  type CamadaGerenciada,
  type ProcessoOpencode,
} from '@remoto/protocolo';
import { MAXIMO_DE_FAVORITOS, type ModeloOpencode } from '@remoto/shared';

/**
 * As regras da tela de Configurações do OpenCode (F17), fora dos componentes.
 *
 * Função pura, e não `if` dentro do JSX, pela regra de sempre: o que decide o
 * que o dono lê sobre a chave da frota e sobre o esforço do modelo precisa de
 * teste próprio, e teste de tela é caro demais para cobrir cada combinação.
 */

/* -------------------------------------------------------------------------- */
/* Dinheiro e tamanho                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Dólar em pt-BR: `US$ 12,50`.
 *
 * Duas casas no mínimo e três no máximo: o preço por milhão de tokens tem
 * modelo a US$ 0,071, e arredondar para 0,07 esconderia a diferença que faz
 * alguém escolher um em vez do outro.
 */
export function dinheiro(usd: number): string {
  return (
    'US$ ' +
    usd.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })
  );
}

/**
 * O contexto em palavras curtas: `1M`, `262 mil`.
 *
 * `1_048_576` também é "1M": o catálogo mistura potência de dois e de dez, e
 * a diferença não muda a escolha de ninguém.
 */
export function tamanhoDoContexto(tokens: number): string {
  if (tokens >= 1_000_000) {
    // Uma casa decimal, e nenhuma quando ela é zero: `1M`, `1,5M`.
    const milhoes = Math.round((tokens / 1_000_000) * 10) / 10;
    return milhoes.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + 'M';
  }
  if (tokens >= 1_000) return Math.round(tokens / 1_000) + ' mil';
  return String(tokens);
}

/**
 * A linha que acompanha um modelo no seletor: preço, contexto, imagem e, no
 * NanoGPT, a assinatura.
 *
 * Preço ausente é "preço não informado", nunca zero: o catálogo não dizer é
 * diferente de o modelo ser de graça, e o zero convidaria a escolhê-lo.
 *
 * "na assinatura" / "fora da assinatura" só quando o catálogo DISSE (F2, D43):
 * no NanoGPT o modelo de fora da assinatura gasta crédito desde a primeira
 * chamada, e é isso que separa dois modelos de preço parecido. Ausente — o
 * OpenRouter, que não tem assinatura — não vira palavra nenhuma.
 */
export function descricaoDoModelo(
  m: Pick<ModeloOpencode, 'entradaUsdPorMTok' | 'saidaUsdPorMTok' | 'contexto' | 'aceitaImagem'> &
    Partial<Pick<ModeloOpencode, 'incluidoNaAssinatura'>>,
): string {
  const partes: string[] = [];
  if (m.entradaUsdPorMTok === null || m.saidaUsdPorMTok === null) {
    partes.push('preço não informado');
  } else {
    partes.push(`${dinheiro(m.entradaUsdPorMTok)} / ${dinheiro(m.saidaUsdPorMTok)} por 1M`);
  }
  if (m.contexto !== null) partes.push(`${tamanhoDoContexto(m.contexto)} de contexto`);
  // Informativo (D23 revista): quem garante que a imagem é lida é o modelo
  // de visão da config, não este selo.
  if (m.aceitaImagem) partes.push('aceita imagem');
  if (m.incluidoNaAssinatura === true) partes.push('na assinatura');
  if (m.incluidoNaAssinatura === false) partes.push('fora da assinatura');
  return partes.join(' · ');
}

/**
 * O `tempoRelativo` dentro de uma frase: "trocada há 2 dias", mas "trocada em
 * 12/09/2026" — passada uma semana ele devolve a data, e "trocada 12/09/2026"
 * não é português.
 */
export function quando(relativo: string): string {
  return /^(há |agora$)/.test(relativo) ? relativo : 'em ' + relativo;
}

/* -------------------------------------------------------------------------- */
/* Esforço                                                                     */
/* -------------------------------------------------------------------------- */

/** Como a tela escreve o `padrao`: não mandar `variant` e deixar o provedor decidir. */
export const ROTULO_DO_ESFORCO_PADRAO = 'Padrão do provedor';

export interface OpcaoDeEsforco {
  id: string;
  rotulo: string;
  descricao?: string;
}

/**
 * As opções de esforço de UM modelo (R7): "padrão do provedor" e os
 * `variant` que aquele modelo tem.
 *
 * **Não é `low/medium/high` fixo, e isso é medição.** O DeepSeek V4.1 Flash
 * tem `[low, high, max]` — sem `medium` —, o V4 Pro tem `[high, xhigh]` e o
 * Kimi não tem nenhum. Uma lista da casa ofereceria esforço que o modelo não
 * aceita, e a API o recusaria depois do clique.
 *
 * `atual` entra na lista quando o modelo não o tem, marcado como tal: sem
 * isso o campo fechado diria "escolha…" em vez de mostrar o que está gravado,
 * e quem olha concluiria que não há esforço nenhum.
 */
export function opcoesDeEsforco(
  modelo: Pick<ModeloOpencode, 'esforcos'> | undefined,
  atual?: string,
): OpcaoDeEsforco[] {
  const opcoes: OpcaoDeEsforco[] = [
    { id: ESFORCO_SEM_VARIANTE, rotulo: ROTULO_DO_ESFORCO_PADRAO, descricao: 'não força nível nenhum' },
    ...(modelo?.esforcos ?? []).map((e) => ({ id: e, rotulo: e })),
  ];
  if (atual !== undefined && !opcoes.some((o) => o.id === atual)) {
    opcoes.push({ id: atual, rotulo: atual, descricao: 'fora da lista deste modelo' });
  }
  return opcoes;
}

/**
 * O esforço que sobrevive à troca de modelo.
 *
 * Fica quando o modelo novo o tem; senão volta ao padrão do provedor. Um
 * `max` escolhido no DeepSeek flash não existe no V4 Pro, e mantê-lo
 * mandaria à API um par que ela recusa (`esforco_indisponivel_no_modelo`).
 * Modelo desconhecido também volta ao padrão: é o único esforço que vale para
 * qualquer modelo.
 */
export function esforcoAposTrocarModelo(
  esforco: string,
  novo: Pick<ModeloOpencode, 'esforcos'> | undefined,
): string {
  if (esforco === ESFORCO_SEM_VARIANTE) return esforco;
  return novo?.esforcos.includes(esforco) ? esforco : ESFORCO_SEM_VARIANTE;
}

/* -------------------------------------------------------------------------- */
/* Favoritos                                                                   */
/* -------------------------------------------------------------------------- */

/** Soma um favorito no fim, sem repetir e sem passar do teto. */
export function adicionarFavorito(favoritos: readonly string[], id: string): string[] {
  if (favoritos.includes(id) || favoritos.length >= MAXIMO_DE_FAVORITOS) return [...favoritos];
  return [...favoritos, id];
}

export function removerFavorito(favoritos: readonly string[], id: string): string[] {
  return favoritos.filter((f) => f !== id);
}

/* -------------------------------------------------------------------------- */
/* A chave numa máquina                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Onde a chave da frota está numa máquina, do jeito que o dono pergunta.
 *
 * - `aplicada`: a máquina roda com a versão ATUAL da chave;
 * - `pendente`: a máquina não tem a versão atual — nunca recebeu, tem uma
 *   anterior, ou ainda tem uma que a frota já removeu;
 * - `aguardando_turnos`: recebeu, e espera os turnos em andamento acabarem
 *   para reiniciar o OpenCode (D9, teto de 10 min);
 * - `reiniciando`: no intervalo curto entre derrubar e subir;
 * - `erro`: recebeu e não conseguiu aplicar — o motivo vem na coluna de erro;
 * - `sem_chave`: a frota não tem chave, e a máquina também não;
 * - `sem_opencode`: a máquina nunca relatou OpenCode (retrato `null`);
 * - `desatualizada`: o daemon dela é anterior ao que fala o provedor ATIVO
 *   (F2: o NanoGPT pede o 0.8.0). A API manda `chave: null` para ela, e o
 *   retrato diz `ausente` — mas o gesto que resolve é atualizar o daemon, não
 *   colar a chave de novo, e por isso a tela não diz "sem chave".
 */
export type SituacaoDaChave =
  | 'aplicada'
  | 'pendente'
  | 'aguardando_turnos'
  | 'reiniciando'
  | 'erro'
  | 'sem_chave'
  | 'sem_opencode'
  | 'desatualizada';

export const ROTULO_DA_SITUACAO: Record<SituacaoDaChave, string> = {
  aplicada: 'aplicada',
  pendente: 'pendente',
  aguardando_turnos: 'aguardando turnos',
  reiniciando: 'reiniciando',
  erro: 'erro',
  sem_chave: 'sem chave',
  sem_opencode: 'sem OpenCode',
  desatualizada: 'daemon desatualizado',
};

/**
 * A situação da chave numa máquina, a partir do último retrato dela.
 *
 * A versão manda, e não o estado sozinho: `aplicada` com a versão 2 quando a
 * frota está na 3 é uma máquina que ainda roda com a chave VELHA — e dizer
 * "aplicada" ali faria o dono acreditar que a troca terminou.
 *
 * **A versão da frota é a do FIO** (`versaoDaCredencial`, F2): a soma das duas
 * chaves com a parcela da configuração. Comparar com a versão da chave do
 * provedor diria "pendente" para sempre — o daemon guarda a soma. `null` é a
 * frota sem chave do provedor ativo.
 *
 * `desatualizada` vem do servidor (o daemon não fala o provedor ativo) e
 * vence o resto, inclusive a falta de retrato: é o gesto que destrava a
 * máquina.
 *
 * `switch` exaustivo sobre o estado: um estado novo no protocolo sem ramo
 * aqui não compila, em vez de cair calado no "aplicada".
 */
export function situacaoDaChave(
  versaoDaFrota: number | null,
  retrato: Pick<AgenteOpencode, 'chave'> | null,
  desatualizada = false,
): SituacaoDaChave {
  if (desatualizada) return 'desatualizada';
  if (!retrato) return 'sem_opencode';
  const { estado, versao } = retrato.chave;
  switch (estado) {
    case 'erro':
      return 'erro';
    case 'aguardando_turnos':
      return 'aguardando_turnos';
    case 'reiniciando':
      return 'reiniciando';
    case 'aplicada':
      if (versaoDaFrota === null) return 'pendente';
      return versao === versaoDaFrota ? 'aplicada' : 'pendente';
    case 'ausente':
      return versaoDaFrota !== null ? 'pendente' : 'sem_chave';
    default: {
      const _nenhum: never = estado;
      return _nenhum;
    }
  }
}

/** A cor da situação: verde só quando não há nada a fazer. */
export function tomDaSituacao(s: SituacaoDaChave): 'bom' | 'espera' | 'ruim' | 'neutro' {
  switch (s) {
    case 'aplicada':
      return 'bom';
    case 'pendente':
    case 'aguardando_turnos':
    case 'reiniciando':
      return 'espera';
    case 'erro':
    case 'desatualizada':
      return 'ruim';
    case 'sem_chave':
    case 'sem_opencode':
      return 'neutro';
    default: {
      const _nenhum: never = s;
      return _nenhum;
    }
  }
}

/**
 * O agregado "aplicada em X de Y", como frase.
 *
 * O número é do SERVIDOR (regra 3 do `api.md`: agregado se calcula no
 * service); aqui só se escolhe a frase. Sem chave, não há o que aplicar — e
 * "aplicada em 0 de 3" pareceria defeito das máquinas.
 */
export function textoDoAgregado(
  aplicadas: { aplicadas: number; elegiveis: number },
  temChave: boolean,
): string {
  if (!temChave) return 'Sem chave cadastrada: nenhuma máquina tem o que aplicar.';
  if (aplicadas.elegiveis === 0) return 'Nenhuma máquina com OpenCode ligado ainda.';
  const plural = aplicadas.elegiveis === 1 ? 'máquina' : 'máquinas';
  return `Chave aplicada em ${aplicadas.aplicadas} de ${aplicadas.elegiveis} ${plural}.`;
}

/**
 * A frase das máquinas com daemon desatualizado, ao lado do agregado — ou
 * `null` quando não há nenhuma.
 *
 * Elas CONTAM no Y e nunca entram no X até serem atualizadas (o servidor as
 * mantém no Y de propósito: escondê-las faria o "Y de Y" mentir). Sem esta
 * frase, o "1 de 4" pareceria uma troca de chave que não terminou, e o dono
 * esperaria um X que nunca vem.
 */
export function textoDasDesatualizadas(quantas: number, nomeDoProvedor: string): string | null {
  if (quantas <= 0) return null;
  return quantas === 1
    ? `1 máquina tem o daemon anterior ao que fala o ${nomeDoProvedor}: ela só recebe a chave depois de atualizada.`
    : `${quantas} máquinas têm o daemon anterior ao que fala o ${nomeDoProvedor}: elas só recebem a chave depois de atualizadas.`;
}

/* -------------------------------------------------------------------------- */
/* O resto do retrato                                                          */
/* -------------------------------------------------------------------------- */

export const ROTULO_DA_CAMADA: Record<CamadaGerenciada, string> = {
  ok: 'ok',
  ausente: 'ausente',
  acl_frouxa: 'ACL frouxa',
  divergente: 'divergente',
};

export interface ProcessoNaTela extends ProcessoOpencode {
  perfil: 'trabalho' | 'mestre';
}

/** Os processos do retrato em ordem fixa: trabalho (PC) e mestre (VPS). */
export function processosDoRetrato(retrato: Pick<AgenteOpencode, 'processos'>): ProcessoNaTela[] {
  const lista: ProcessoNaTela[] = [];
  if (retrato.processos.trabalho) lista.push({ perfil: 'trabalho', ...retrato.processos.trabalho });
  if (retrato.processos.mestre) lista.push({ perfil: 'mestre', ...retrato.processos.mestre });
  return lista;
}

/**
 * Um processo em uma linha: `trabalho: de pé · 345 MB · 2 sessões`.
 *
 * RSS `null` é "não medido" e some da frase — escrever "0 MB" seria o painel
 * afirmando uma medida que ninguém fez.
 */
export function textoDoProcesso(p: ProcessoNaTela): string {
  const partes = [`${p.perfil}: ${p.dePe ? 'de pé' : 'parado'}`];
  if (p.rssMb !== null) partes.push(`${Math.round(p.rssMb)} MB`);
  partes.push(`${p.sessoes} ${p.sessoes === 1 ? 'sessão' : 'sessões'}`);
  return partes.join(' · ');
}
